import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { briefTemplatesQueryOptions, type BriefTemplate } from '@/entities/brief-template';
import { usePermissions } from '@/entities/user';
import { notificationService } from '@/lib/services/notification.service';
import { ROUTES } from '@/shared/config/routes';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';
import type {
  BriefTemplateLevelFilter,
  BriefTemplateStatusFilter,
} from '../../constants/briefTemplates.constants';

/** `description` is excluded — it's an opaque TipTap doc, not searchable as plain text. */
function matchesSearch(template: BriefTemplate, term: string): boolean {
  if (!term) return true;
  return template.name.toLowerCase().includes(term);
}

function matchesLevel(template: BriefTemplate, filter: BriefTemplateLevelFilter): boolean {
  return filter === 'all' || template.level === filter;
}

function matchesStatus(template: BriefTemplate, filter: BriefTemplateStatusFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'off') return !template.enabled;
  return template.enabled && template.status === filter;
}

export function useBriefTemplatesList() {
  const { t } = useTranslation('knowledge');
  const navigate = useNavigate();
  const permissions = usePermissions();
  const levelsEnabled = useBooleanFlagValue('brief_template_levels', false);
  // A new template is a workspace or an organization one, so a system-only manager creates none.
  const canCreate = levelsEnabled
    ? permissions.canManageWorkspaceBriefTemplates ||
      permissions.canManageOrganizationBriefTemplates
    : permissions.canManageBriefTemplates;

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<BriefTemplateLevelFilter>('all');
  const [statusFilter, setStatusFilter] = useState<BriefTemplateStatusFilter>('all');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const query = useQuery(briefTemplatesQueryOptions());

  // Memoize so the `?? []` fallback keeps a stable reference between renders.
  const templates = useMemo<BriefTemplate[]>(() => query.data ?? [], [query.data]);

  // With the flag off the filters are not on screen, so they must not narrow the list either.
  const filtersActive = levelsEnabled && (levelFilter !== 'all' || statusFilter !== 'all');

  const filteredTemplates = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    return templates.filter(
      (template) =>
        matchesSearch(template, term) &&
        (!levelsEnabled ||
          (matchesLevel(template, levelFilter) && matchesStatus(template, statusFilter))),
    );
  }, [templates, debouncedSearch, levelsEnabled, levelFilter, statusFilter]);

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setLevelFilter('all');
    setStatusFilter('all');
  }, []);

  const handleOpenCreate = useCallback(() => setCreateModalOpen(true), []);
  const handleCloseCreate = useCallback(() => setCreateModalOpen(false), []);

  const handleIntakeCreated = useCallback(
    (templateId: string) => {
      notificationService.showSuccess(t('knowledgeHub.briefTemplatesList.newTemplateCreated'));
      // Close before navigate so the modal unmounts and its form state resets.
      setCreateModalOpen(false);
      void navigate(ROUTES.KNOWLEDGE_HUB_BRIEF_TEMPLATE_DETAIL(templateId));
    },
    [navigate, t],
  );

  const { refetch } = query;
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const searchActive = debouncedSearch.trim().length > 0;
  const showError = query.isError && templates.length === 0;
  const showInitialEmpty = !query.isLoading && !query.isError && templates.length === 0;
  const showNoResults =
    !query.isLoading &&
    !query.isError &&
    templates.length > 0 &&
    filteredTemplates.length === 0 &&
    (searchActive || filtersActive);

  return {
    canCreate,
    levelsEnabled,
    search,
    levelFilter,
    statusFilter,
    filtersActive,
    filteredTemplates,
    isLoading: query.isLoading,
    createModalOpen,
    isAboutOpen,
    showError,
    showInitialEmpty,
    showNoResults,
    // Hide the toolbar in the true-empty + error states (mirrors the Projects
    // list) — with no templates there's nothing to search/filter.
    showToolbar: !showInitialEmpty && !showError,
    handlers: {
      searchChange: setSearch,
      levelFilterChange: setLevelFilter,
      statusFilterChange: setStatusFilter,
      clearFilters: handleClearFilters,
      openCreate: handleOpenCreate,
      closeCreate: handleCloseCreate,
      intakeCreated: handleIntakeCreated,
      retry: handleRetry,
      setAboutOpen: setIsAboutOpen,
    },
  };
}
